import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Table,
  Space,
  Divider,
} from "antd";
import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import {
  PrinterOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { API, Headers } from "../../Config";
import "./style.css";

const FormModal = ({
  visible,
  onClose,
  mode = "post",
  item,
  page_mode,
  columnsVisible,
  accounts = [],
  defaultCurrency,
  onchange,
}) => {
  const { id: userId } = useSelector((state) => state.Auth.user);
  const [form] = Form.useForm();
  const dateFormat = "DD/MM/YYYY";
  const printRef = useRef();

  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(
    item?.voucherDetails?.[0]?.account_no
  );
  const [selectedType, setSelectedType] = useState(() => {
    if (mode === "put") {
      if (item?.voucher_type === "OP") return "other";
      if (item?.purchase_id) return "purchase";
      return "awb";
    }
    return "awb";
  });
  const [selectedAgentList, setSelectedAgentList] = useState();
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [paymentAmounts, setPaymentAmounts] = useState({});

  // Other Payment form fields
  const [otherPaymentItems, setOtherPaymentItems] = useState(() => {
    if (mode === "put" && item?.voucher_type === "OP" && item?.voucherDetails) {
      // Pre-fill with existing voucher details for edit mode
      return item.voucherDetails.map((detail, index) => ({
        id: detail.key || index + 1,
        account_no: detail.account_no || "",
        description: detail.description || "",
        amount: detail.amount_cr ? detail.amount_cr.toString() : "",
      }));
    }
    return [{ id: 1, account_no: "", description: "", amount: "" }];
  });

  const baseColumns = useMemo(
    () => [
      {
        title: "#",
        key: "index",
        width: "50px",
        align: "center",
        render: (_, __, index) => index + 1,
      },
      {
        title: "Account No",
        dataIndex: "account_no",
        key: "account_no",
        align: "center",
      },
      {
        title: selectedType === "awb" ? "Bill Name" : "Purchase Name",
        dataIndex: selectedType === "awb" ? "awb" : "name",
        key: selectedType === "awb" ? "awb" : "name",
        align: "center",
        render: (value) => {
          if (selectedType === "awb") {
            return value ? `${value?.airline} - ${value?.awb_no}` : "-";
          }
          return value || "-";
        },
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        align: "center",
      },
      {
        title: "Amount",
        dataIndex: selectedType === "awb" ? "amount" : "total",
        key: "amount",
        align: "center",
        render: (amount) => amount,
      },
      {
        title: "Paid Amount",
        dataIndex: "paidAmount",
        key: "paidAmount",
        align: "center",
        render: (amount) => amount || "0.00",
      },
    ],
    [selectedType]
  );

  const selectedColumns = useMemo(
    () => [
      ...baseColumns,
      {
        title: "Pay Amount",
        key: "paymentAmount",
        align: "center",
        render: (_, record) => {
          const amount =
            Number(record?.[selectedType === "awb" ? "amount" : "total"]) || 0;
          const paidAmount = Number(record?.paidAmount) || 0;
          const isFullyPaid = amount === paidAmount;

          return (
            <Input
              // type="number"
              size="small"
              value={isFullyPaid ? 0 : paymentAmounts[record?.id]}
              onChange={(e) => handlePaymentChange(record, e.target.value)}
              style={{ width: "100%" }}
              max={amount - paidAmount}
              min={0}
              disabled={isFullyPaid}
              placeholder={isFullyPaid ? "Fully Paid" : "Enter amount"}
            />
          );
        },
      },
    ],
    [baseColumns, paymentAmounts, selectedType]
  );

  const fullyPaidColumns = useMemo(
    () => [
      ...baseColumns,
      {
        title: "Status",
        key: "status",
        align: "center",
        render: () => "Fully Paid",
      },
    ],
    [baseColumns]
  );

  // Other Payment columns
  const otherPaymentColumns = useMemo(
    () => [
      {
        title: "#",
        key: "index",
        width: "50px",
        align: "center",
        render: (_, __, index) => index + 1,
      },
      {
        title: "Account No",
        key: "account_no",
        align: "center",
        width: "350px",
        render: (_, record) => (
          <Select
            size="small"
            placeholder="Select account"
            value={record.account_no}
            onChange={(value) =>
              handleOtherPaymentChange(record.id, "account_no", value)
            }
            style={{ width: "100%" }}
            showSearch
            allowClear
            filterOption={(input, option) => {
              const accountNo = option.value?.toLowerCase() || "";
              const accountName =
                option.children?.toString().toLowerCase() || "";
              const searchTerm = input.toLowerCase();
              return (
                accountNo.includes(searchTerm) ||
                accountName.includes(searchTerm)
              );
            }}
          >
            {accounts.map((item) => (
              <Select.Option key={item.account_no} value={item.account_no}>
                {item.account_no} - {item.account_name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        title: "Description",
        key: "description",
        align: "center",
        render: (_, record) => (
          <Input
            size="small"
            placeholder="Enter description"
            value={record.description}
            onChange={(e) =>
              handleOtherPaymentChange(record.id, "description", e.target.value)
            }
          />
        ),
      },
      {
        title: "Amount",
        key: "amount",
        align: "center",
        render: (_, record) => (
          <Input
            type="number"
            size="small"
            placeholder="Enter amount"
            value={record.amount}
            onChange={(e) =>
              handleOtherPaymentChange(record.id, "amount", e.target.value)
            }
          />
        ),
      },
      {
        title: "Action",
        key: "action",
        align: "center",
        width: "80px",
        render: (_, record) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveOtherPaymentItem(record.id)}
            disabled={otherPaymentItems.length === 1}
          />
        ),
      },
    ],
    [otherPaymentItems, accounts]
  );

  useEffect(() => {
    if (mode === "put" && selectedAgentList?.data) {
      // Get voucher detail keys for filtering items
      const voucherDetailKeys =
        item?.voucherDetails?.map((detail) => detail.key.toString()) || [];

      const flatData =
        selectedType === "awb"
          ? selectedAgentList?.data?.awbPayments
              ?.map((item) =>
                item?.awbPaymentDetails
                  .filter((paymentDetail) => {
                    // In edit mode, only show AWB payment details that match voucher detail keys
                    if (mode === "put" && selectedType === "awb") {
                      return voucherDetailKeys.includes(
                        paymentDetail.id.toString()
                      );
                    }
                    return true;
                  })
                  .map((paymentDetail) => ({
                    ...paymentDetail,
                    awb: item.awb,
                    billName: `${item.awb?.airline} - ${item.awb?.awb_no} - ${item.awb?.orijin_airport_name} → ${item.awb?.destination_airport_name}`,
                  }))
              )
              .flat()
              .filter(Boolean) || []
          : selectedAgentList?.data?.purchase
              ?.map((item) =>
                item?.purchaseDetails
                  .filter((detail) => {
                    // In edit mode, only show purchase items that match voucher detail keys
                    if (mode === "put" && selectedType === "purchase") {
                      return voucherDetailKeys.includes(detail.id);
                    }
                    return true;
                  })
                  .map((detail) => ({
                    ...detail,
                    account_no: item.supplierDetails.account_no,
                    description: item.description,
                    total: detail.total,
                    paidAmount: detail.paid_amount,
                  }))
              )
              .flat()
              .filter(Boolean) || [];

      const allRowKeys = flatData
        .filter((row) => {
          const amount =
            Number(row?.[selectedType === "awb" ? "amount" : "total"]) || 0;
          const paidAmount = Number(row?.paidAmount) || 0;
          return amount !== paidAmount;
        })
        .map((row) => row.id);
      setSelectedRows(allRowKeys);
      setSelectedData(flatData.filter((row) => allRowKeys.includes(row.id)));

      const initialPaymentAmounts = flatData.reduce((acc, row) => {
        const amount =
          Number(row?.[selectedType === "awb" ? "amount" : "total"]) || 0;
        const paidAmount = Number(row?.paidAmount) || 0;
        const remainingAmount = amount - paidAmount;
        return {
          ...acc,
          [row.id]: remainingAmount > 0 ? remainingAmount : 0,
        };
      }, {});
      setPaymentAmounts(initialPaymentAmounts);
    }
  }, [mode, selectedAgentList, selectedType, item?.voucherDetails]);

  useEffect(() => {
    if (selectedAgent && selectedType !== "other") {
      getAgentPaymentList(selectedAgent);
    }
  }, [selectedAgent, selectedType]);

  useEffect(() => {
    setSelectedData([]);
    setSelectedRows([]);
    setPaymentAmounts({});
    if (selectedType === "other" && mode !== "put") {
      // Only reset for new vouchers, not for edit mode
      setOtherPaymentItems([
        { id: 1, account_no: "", description: "", amount: "" },
      ]);
    }
  }, [selectedType, mode]);

  // Handle other payment form changes
  const handleOtherPaymentChange = (id, field, value) => {
    setOtherPaymentItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  // Add new other payment item
  const handleAddOtherPaymentItem = () => {
    const newId = Math.max(...otherPaymentItems.map((item) => item.id), 0) + 1;
    setOtherPaymentItems((prev) => [
      ...prev,
      { id: newId, account_no: "", description: "", amount: "" },
    ]);
  };

  // Remove other payment item
  const handleRemoveOtherPaymentItem = (id) => {
    if (otherPaymentItems.length > 1) {
      setOtherPaymentItems((prev) => prev.filter((item) => item.id !== id));
    }
  };

  const getAgentPaymentList = async (id) => {
    try {
      const response = await axios.get(
        `${API.BASR_URL}${API.GET_AGENT_REGISTRATION_LIST}${id}/${mode}`,
        { headers: Headers() }
      );
      setSelectedAgentList(response?.data);
    } catch (error) {
      message.error("Failed to fetch agent payment list");
    }
  };

  const handlePaymentChange = (record, value) => {
    const numValue = parseFloat(value) || 0;
    const amount =
      Number(record?.[selectedType === "awb" ? "amount" : "total"]) || 0;
    const paidAmount = Number(record?.paidAmount) || 0;
    const remainingAmount = amount - paidAmount;
    const validatedValue = Math.min(numValue, remainingAmount);

    if (remainingAmount <= 0) return;

    setPaymentAmounts((prev) => ({
      ...prev,
      [record.id]: validatedValue,
    }));

    setSelectedData((prev) =>
      prev.map((item) =>
        item.id === record.id
          ? { ...item, paymentAmount: validatedValue }
          : item
      )
    );
  };

  const handleRowSelection = (selectedRowKeys, rows) => {
    setSelectedRows(selectedRowKeys);

    const updatedSelectedData =
      selectedType === "awb"
        ? selectedAgentList?.data?.awbPayments
            ?.map((item) =>
              item?.awbPaymentDetails
                ?.filter((paymentDetail) => {
                  // In edit mode, only show AWB payment details that match voucher detail keys
                  if (mode === "put" && selectedType === "awb") {
                    return voucherDetailKeys.includes(
                      paymentDetail.id.toString()
                    );
                  }
                  return true;
                })
                .map((paymentDetail) => ({
                  ...paymentDetail,
                  awb: item?.awb,
                  billName: `${item?.awb?.airline} - ${item?.awb?.awb_no} - ${item?.awb?.orijin_airport_name} → ${item?.awb?.destination_airport_name}`,
                }))
            )
            .flat()
            .filter((row) => selectedRowKeys.includes(row.id))
        : selectedAgentList?.data?.purchase
            ?.map((item) =>
              item?.purchaseDetails
                ?.filter((detail) => {
                  // In edit mode, only show purchase items that match voucher detail keys
                  if (mode === "put" && selectedType === "purchase") {
                    return voucherDetailKeys.includes(detail.id);
                  }
                  return true;
                })
                .map((detail) => ({
                  ...detail,
                  account_no: item?.supplierDetails?.account_no,
                  description: item?.description,
                  total: detail?.total,
                  paidAmount: detail?.paid_amount,
                }))
            )
            .flat()
            .filter((row) => selectedRowKeys.includes(row.id));

    setSelectedData(updatedSelectedData);

    const newPaymentAmounts = updatedSelectedData.reduce((acc, row) => {
      const amount =
        Number(row?.[selectedType === "awb" ? "amount" : "total"]) || 0;
      const paidAmount = Number(row?.paidAmount) || 0;
      const remainingAmount = amount - paidAmount;
      return {
        ...acc,
        [row.id]:
          mode === "put"
            ? remainingAmount > 0
              ? remainingAmount
              : 0
            : paymentAmounts[row.id] ||
              (remainingAmount > 0 ? remainingAmount : 0),
      };
    }, {});
    setPaymentAmounts(newPaymentAmounts);
  };

  const purchaseId =
    selectedType === "awb" ? "" : selectedData?.[0]?.purchase_id;

  const handleSubmit = async (values) => {
    // Validation for all types
    if (selectedType === "other") {
      const validItems = otherPaymentItems.filter(
        (item) => item.account_no && item.description && item.amount
      );

      if (validItems.length === 0) {
        message.warning("Please add at least one valid payment item");
        return;
      }

      const totalAmount = validItems.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
      );

      if (totalAmount <= 0) {
        message.warning("Total amount must be greater than 0");
        return;
      }
    } else {
      if (selectedData.length === 0) {
        message.warning("Please select at least one item");
        return;
      }
    }

    setLoading(true);
    try {
      // Prepare voucher items based on type
      let transformedVoucherItems = [];
      let crTotal = 0;
      let drTotal = 0;
      let totalPaidAmount = 0;

      if (selectedType === "other") {
        // Other Payment type
        const validItems = otherPaymentItems.filter(
          (item) => item.account_no && item.description && item.amount
        );

        transformedVoucherItems = validItems.map((item) => ({
          key: item.id,
          account_no: item.account_no,
          description: item.description,
          amount_cr: Number(item.amount || 0),
          amount_dr: Number(item.amount || 0),
          payment_amount: Number(item.amount || 0),
          airway_bill: null,
          airway_bill_payment_id: null,
          purchase_id: null,
          airwaybill_and_airline: "",
        }));

        crTotal = validItems.reduce(
          (sum, item) => sum + Number(item.amount || 0),
          0
        );
        drTotal = crTotal;
        totalPaidAmount = crTotal;
      } else {
        // AWB and Purchase types
        transformedVoucherItems = selectedData?.map((item) => {
          const paidAmount = Number(item?.paidAmount) || 0;
          const newPaymentAmount = Number(paymentAmounts[item.id]) || 0;
          return {
            key: item?.id,
            account_no: item?.account_no,
            description:
              selectedType === "awb"
                ? item?.description
                : `${item?.description} - ${item?.name}`,
            amount_cr: Number(
              item?.[selectedType === "awb" ? "amount" : "total"] || 0
            ),
            amount_dr: newPaymentAmount + paidAmount,
            payment_amount: newPaymentAmount,
            airway_bill: selectedType === "awb" ? item?.AwbPayment_id : null,
            airway_bill_payment_id:
              selectedType === "awb" ? item?.awb?.id : null,
            purchase_id: selectedType === "purchase" ? item?.purchase_id : null,
            airwaybill_and_airline:
              selectedType === "awb"
                ? `${item?.awb?.airline} - ${item?.awb?.awb_no}`
                : "",
          };
        });

        const totals = transformedVoucherItems.reduce(
          (acc, item) => ({
            cr: acc.cr + item.amount_cr,
            dr: acc.dr + item.amount_dr,
          }),
          { cr: 0, dr: 0 }
        );

        crTotal = totals.cr;
        drTotal = selectedData.reduce((sum, item) => {
          const paidAmount = Number(item?.paidAmount) || 0;
          const newPaymentAmount = Number(paymentAmounts[item.id]) || 0;
          return sum + paidAmount + newPaymentAmount;
        }, 0);

        totalPaidAmount = selectedData?.reduce((sum, item) => {
          const newPaymentAmount = Number(paymentAmounts[item?.id]) || 0;
          return sum + newPaymentAmount;
        }, 0);
      }

      // Get agent name (only for AWB and Purchase types)
      let agentName = null;
      if (selectedType !== "other") {
        const selectedAgentData = accounts?.find(
          (acc) => acc?.account_no === selectedAgent
        );
        agentName = selectedAgentData ? selectedAgentData?.account_name : null;
      }

      // Determine voucher type
      const getVoucherType = () => {
        if (selectedType === "other") return "OP";
        if (selectedType === "purchase") return "SP";
        return page_mode;
      };

      // Prepare unified form data
      const formData = {
        voucher_date: values.voucher_date,
        voucher_type: getVoucherType(),
        account_no: values.account_no ?? null,
        agentName: agentName,
        description: values?.description ?? "",
        userid: Number(userId),
        cr_total: crTotal,
        dr_total: drTotal,
        total_paid_amount: totalPaidAmount,
        purchase_id: selectedType === "other" ? null : purchaseId,
        voucherItems: transformedVoucherItems,
      };

      // Unified API call
      const url =
        mode === "post"
          ? `${API.BASR_URL}${API.VOUCHER_ACTIONS}${page_mode}`
          : `${API.BASR_URL}${API.VOUCHER_ACTIONS}${item.id}`;

      const response = await axios({
        method: mode,
        url,
        headers: Headers(),
        data: formData,
      });

      if ([200, 201].includes(response.status)) {
        message.success("Transaction successful");
        onchange();
        onClose();
      }
    } catch (error) {
      message.error("Transaction failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderSelectedSummary = () => {
    const totalPayAmount = selectedData?.reduce((sum, item) => {
      const paymentAmount = paymentAmounts[item.id] || 0;
      return sum + paymentAmount;
    }, 0);

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={3}>
          <strong>Total Pay Amount</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell />
        <Table.Summary.Cell />
        <Table.Summary.Cell />
        <Table.Summary.Cell index={3}>
          <strong>{totalPayAmount.toFixed(2)}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  // Other Payment summary
  const renderOtherPaymentSummary = () => {
    const totalAmount = otherPaymentItems.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={3}>
          <strong>Total Amount</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3}>
          <strong>{totalAmount.toFixed(2)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell />
      </Table.Summary.Row>
    );
  };

  const getAccountDetails = (accountNo) => {
    const account = accounts.find((acc) => acc.account_no === accountNo);
    return account
      ? `${account.account_no} - ${account.account_name}`
      : accountNo || "-";
  };

  const getPrintColumns = () => {
    return [
      {
        title: "#",
        key: "index",
        width: "50px",
        align: "center",
        render: (_, __, index) => index + 1,
      },
      {
        title: "Account No",
        dataIndex: "account_no",
        key: "account_no",
        align: "center",
        render: (text) => {
          const account = accounts.find((acc) => acc.account_no === text);
          return account
            ? `${account.account_no} - ${account.account_name}`
            : text || "-";
        },
      },
      {
        title: selectedType === "awb" ? "Bill Name" : "Purchase Name",
        dataIndex: selectedType === "awb" ? "awb" : "name",
        key: selectedType === "awb" ? "awb" : "name",
        align: "center",
        render: (value) => {
          if (selectedType === "awb") {
            return value ? `${value?.airline} - ${value?.awb_no}` : "-";
          }
          return value || "-";
        },
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        align: "center",
      },
      {
        title: "Amount",
        dataIndex: selectedType === "awb" ? "amount" : "total",
        key: "amount",
        align: "center",
        render: (amount) => Number(amount || 0).toFixed(2),
      },
      {
        title: "Paid Amount",
        dataIndex: "paidAmount",
        key: "paidAmount",
        align: "center",
        render: (amount) => Number(amount || 0).toFixed(2),
      },
    ];
  };

  // Get voucher detail keys for filtering purchase items in edit mode
  const voucherDetailKeys =
    item?.voucherDetails?.map((detail) => detail.key.toString()) || [];

  const fullyPaidData =
    selectedType === "awb"
      ? selectedAgentList?.data?.awbPayments
          ?.map((item) =>
            item?.awbPaymentDetails
              ?.filter((paymentDetail) => {
                // In edit mode, only show AWB payment details that match voucher detail keys
                if (mode === "put" && selectedType === "awb") {
                  return voucherDetailKeys.includes(
                    paymentDetail.id.toString()
                  );
                }
                return true;
              })
              .map((paymentDetail) => ({
                ...paymentDetail,
                awb: item?.awb,
              }))
          )
          .flat()
          .filter((item) => {
            const amount =
              Number(item?.[selectedType === "awb" ? "amount" : "total"]) || 0;
            const paidAmount = Number(item?.paidAmount) || 0;
            return amount === paidAmount;
          }) || []
      : selectedAgentList?.data?.purchase
          ?.map((item) =>
            item?.purchaseDetails
              ?.filter((detail) => {
                // In edit mode, only show purchase items that match voucher detail keys
                if (mode === "put" && selectedType === "purchase") {
                  return voucherDetailKeys.includes(detail.id);
                }
                return true;
              })
              .map((detail) => ({
                ...detail,
                account_no: item?.supplierDetails?.account_no,
                description: detail?.description || "",
                total: detail?.total,
                paidAmount: detail?.paid_amount,
              }))
          )
          .flat()
          .filter((item) => {
            const amount =
              Number(item?.[selectedType === "awb" ? "amount" : "total"]) || 0;
            const paidAmount = Number(item?.paidAmount) || 0;
            return amount === paidAmount;
          }) || [];

  const unpaidData =
    selectedType === "awb"
      ? selectedAgentList?.data?.awbPayments
          ?.map((item) =>
            item?.awbPaymentDetails
              ?.filter((paymentDetail) => {
                // In edit mode, only show AWB payment details that match voucher detail keys
                if (mode === "put" && selectedType === "awb") {
                  return voucherDetailKeys.includes(
                    paymentDetail.id.toString()
                  );
                }
                return true;
              })
              .map((paymentDetail) => ({
                ...paymentDetail,
                awb: item?.awb,
              }))
          )
          .flat()
          .filter((item) => {
            const amount =
              Number(item?.[selectedType === "awb" ? "amount" : "total"]) || 0;
            const paidAmount = Number(item?.paidAmount) || 0;
            return amount !== paidAmount;
          }) || []
      : selectedAgentList?.data?.purchase
          ?.map((item) =>
            item?.purchaseDetails
              ?.filter((detail) => {
                // In edit mode, only show purchase items that match voucher detail keys
                if (mode === "put" && selectedType === "purchase") {
                  return voucherDetailKeys.includes(detail.id);
                }
                return true;
              })
              .map((detail) => ({
                ...detail,
                account_no: item?.supplierDetails?.account_no,
                description: detail?.description || "",
                total: detail?.total,
                paidAmount: detail?.paid_amount,
              }))
          )
          .flat()
          .filter((item) => {
            const amount =
              Number(item?.[selectedType === "awb" ? "amount" : "total"]) || 0;
            const paidAmount = Number(item?.paidAmount) || 0;
            return amount !== paidAmount;
          }) || [];

  const renderPrintSummary = (dataSource) => {
    const totalAmount = dataSource?.reduce((sum, item) => {
      const amount =
        Number(item?.[selectedType === "awb" ? "amount" : "total"]) || 0;
      return sum + amount;
    }, 0);
    const totalPaidAmount = dataSource?.reduce((sum, item) => {
      const paidAmount = Number(item?.paidAmount) || 0;
      return sum + paidAmount;
    }, 0);

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell colSpan={4}>
          <strong>Total</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="center">
          <strong>{Number(totalAmount).toFixed(2)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="center">
          <strong>{Number(totalPaidAmount).toFixed(2)}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  // console.log(
  //   item?.voucherDetails,
  //   "Voucher Details Key means Purchase Item Id "
  // );
  // console.log("Voucher Detail Keys:", voucherDetailKeys);
  // console.log("Selected Agent List:", selectedAgentList?.data?.purchase);

  return (
    <div>
      <Modal
        title={columnsVisible?.page}
        centered
        open={visible}
        onOk={onClose}
        onCancel={onClose}
        width={1000}
        footer={false}
        style={{ borderRadius: 10 }}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          onFinishFailed={() =>
            message.error("Please check all required fields")
          }
          initialValues={{
            voucher_date:
              mode === "put" && item?.voucher_date
                ? moment(item.voucher_date)
                : moment(),
            currency_id: defaultCurrency,
            description: item?.description,
            account_no: item?.account_no,
            agent:
              selectedType !== "other"
                ? item?.voucherDetails?.[0]?.account_no
                : undefined,
            type: selectedType,
          }}
          hideRequiredMark
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              {page_mode !== "JV" && (
                <Form.Item
                  style={{ marginBottom: "0px" }}
                  labelCol={{ span: 7 }}
                  wrapperCol={{ span: 16 }}
                  name="account_no"
                  label="Account"
                  rules={[{ required: true, message: "Account is required" }]}
                >
                  <Select
                    size="small"
                    placeholder="Select account"
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const accountNo = option.value?.toLowerCase() || "";
                      const accountName =
                        option.children?.toString().toLowerCase() || "";
                      const searchTerm = input.toLowerCase();
                      return (
                        accountNo.includes(searchTerm) ||
                        accountName.includes(searchTerm)
                      );
                    }}
                  >
                    {accounts.map((item) => (
                      <Select.Option
                        key={item.account_no}
                        value={item.account_no}
                      >
                        {item.account_no} - {item.account_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {selectedType !== "other" && (
                <Form.Item
                  labelCol={{ span: 7 }}
                  wrapperCol={{ span: 16 }}
                  style={{ marginBottom: "0px" }}
                  name="agent"
                  label="Agent"
                  rules={[{ required: true, message: "Agent is required" }]}
                >
                  <Select
                    size="small"
                    placeholder="Select agent"
                    showSearch
                    allowClear
                    onChange={setSelectedAgent}
                    filterOption={(input, option) => {
                      const accountNo = option.value?.toLowerCase() || "";
                      const accountName =
                        option.children?.toString().toLowerCase() || "";
                      const searchTerm = input.toLowerCase();
                      return (
                        accountNo.includes(searchTerm) ||
                        accountName.includes(searchTerm)
                      );
                    }}
                  >
                    {accounts.map((item) => (
                      <Select.Option
                        key={item.account_no}
                        value={item.account_no}
                      >
                        {item.account_no} - {item.account_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 16 }}
                style={{ marginBottom: "0px" }}
                name="type"
                label="Type"
                rules={[{ required: true, message: "Type is required" }]}
              >
                <Select
                  size="small"
                  placeholder="Select type"
                  onChange={setSelectedType}
                  value={selectedType}
                >
                  <Select.Option value="awb">AWB</Select.Option>
                  <Select.Option value="purchase">Purchase</Select.Option>
                  <Select.Option value="other">Other Payment</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 7 }}
                style={{ marginBottom: "0px" }}
                name="voucher_date"
                label="Date"
                rules={[{ required: true, message: "Date is required" }]}
              >
                <DatePicker
                  size="small"
                  format={dateFormat}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 16 }}
                style={{ marginBottom: "0px" }}
                name="description"
                label="Description"
              >
                <Input.TextArea
                  rows={2}
                  size="small"
                  placeholder="Enter description"
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="mb-4">
            {selectedType === "other" ? (
              <div className="mt-4">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <h6>Other Payment Items</h6>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddOtherPaymentItem}
                    size="small"
                  >
                    Add Item
                  </Button>
                </div>
                <Table
                  dataSource={otherPaymentItems}
                  columns={otherPaymentColumns}
                  pagination={false}
                  size="small"
                  bordered
                  summary={renderOtherPaymentSummary}
                  rowKey="id"
                />
              </div>
            ) : (
              <>
                {fullyPaidData?.length > 0 && mode === "put" && (
                  <div className="mt-4">
                    <h6>
                      Fully Paid{" "}
                      {selectedType === "awb" ? "Airway Bill" : "Purchase"}
                    </h6>
                    <Table
                      dataSource={fullyPaidData}
                      columns={fullyPaidColumns}
                      pagination={false}
                      size="small"
                      bordered
                      rowKey="id"
                    />
                  </div>
                )}

                <div className="mt-4">
                  <h6>
                    {selectedType === "awb" ? "Airway Bill" : "Purchase"}{" "}
                  </h6>
                  <Table
                    dataSource={unpaidData}
                    columns={baseColumns}
                    rowSelection={{
                      onChange: handleRowSelection,
                      selectedRowKeys: selectedRows,
                      defaultSelectedRowKeys:
                        mode === "put" ? selectedRows : undefined,
                      getCheckboxProps: (record) => {
                        const amount =
                          Number(
                            record?.[
                              selectedType === "awb" ? "amount" : "total"
                            ]
                          ) || 0;
                        const paidAmount = Number(record?.paidAmount) || 0;
                        const isFullyPaid = amount === paidAmount;
                        return {
                          disabled: isFullyPaid,
                        };
                      },
                    }}
                    pagination={false}
                    size="small"
                    bordered
                    rowKey="id"
                  />
                </div>
              </>
            )}
          </div>

          {selectedData?.length > 0 && selectedType !== "other" && (
            <div className="mt-4">
              <h6>
                Selected {selectedType === "awb" ? "Airway Bill" : "Purchase"}
              </h6>
              <Table
                dataSource={selectedData}
                columns={selectedColumns}
                pagination={false}
                size="small"
                bordered
                summary={renderSelectedSummary}
                rowKey="id"
              />
            </div>
          )}

          <Row justify="end" className="mt-4">
            <Col xs={24} sm={12} md={6} className="px-2 mb-2">
              <ReactToPrint
                trigger={() => (
                  <Button icon={<PrinterOutlined />} block>
                    Print
                  </Button>
                )}
                content={() => printRef.current}
              />
            </Col>
            <Col xs={24} sm={12} md={6} className="px-2 mb-2">
              <Button onClick={onClose} block>
                Cancel
              </Button>
            </Col>
            <Col xs={24} sm={12} md={6} className="px-2">
              <Button type="primary" htmlType="submit" loading={loading} block>
                {mode === "post" ? "Save" : "Update"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <div style={{ display: "none" }}>
        <div
          ref={printRef}
          style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
        >
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <h2>{columnsVisible?.page || "Voucher"}</h2>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <strong>Date:</strong>{" "}
                  {form.getFieldValue("voucher_date")
                    ? moment(form.getFieldValue("voucher_date")).format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </div>
                {page_mode !== "JV" && (
                  <div>
                    <strong>Account:</strong>{" "}
                    {getAccountDetails(form.getFieldValue("account_no"))}
                  </div>
                )}
                {selectedType !== "other" && (
                  <div>
                    <strong>Agent:</strong>{" "}
                    {getAccountDetails(form.getFieldValue("agent"))}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <div>
                  <strong>Description:</strong>{" "}
                  {form.getFieldValue("description") || ""}
                </div>
                <div>
                  <strong>Type:</strong>{" "}
                  {selectedType === "awb"
                    ? "AWB"
                    : selectedType === "purchase"
                    ? "Purchase"
                    : "Other Payment"}
                </div>
              </Col>
            </Row>
          </div>

          {selectedType === "other" ? (
            <div className="mt-4">
              <h5>Other Payment Items</h5>
              <Table
                size="small"
                columns={[
                  {
                    title: "#",
                    key: "index",
                    width: "50px",
                    align: "center",
                    render: (_, __, index) => index + 1,
                  },
                  {
                    title: "Account No",
                    dataIndex: "account_no",
                    key: "account_no",
                    align: "center",
                    render: (text) => getAccountDetails(text),
                    width: 350,
                  },
                  {
                    title: "Description",
                    dataIndex: "description",
                    key: "description",
                    align: "center",
                  },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    align: "center",
                    render: (amount) => Number(amount || 0).toFixed(2),
                  },
                ]}
                dataSource={otherPaymentItems.filter(
                  (item) => item.account_no && item.description && item.amount
                )}
                pagination={false}
                bordered
                summary={() => {
                  const totalAmount = otherPaymentItems
                    .filter(
                      (item) =>
                        item.account_no && item.description && item.amount
                    )
                    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={3}>
                        <strong>Total Amount</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="center">
                        <strong>{totalAmount.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
                rowKey="id"
              />
            </div>
          ) : (
            <>
              {fullyPaidData?.length > 0 && (
                <div className="mt-4">
                  <h5>
                    Fully Paid{" "}
                    {selectedType === "awb" ? "Airway Bill" : "Purchase"}
                  </h5>
                  <Table
                    size="small"
                    columns={getPrintColumns()}
                    dataSource={fullyPaidData}
                    pagination={false}
                    bordered
                    summary={() => renderPrintSummary(fullyPaidData)}
                    rowKey="id"
                  />
                </div>
              )}
            </>
          )}

          <div style={{ marginTop: "20px", textAlign: "right" }}>
            {selectedType === "other" ? (
              <div>
                <strong>
                  Total Amount:{" "}
                  {Number(
                    otherPaymentItems
                      .filter(
                        (item) =>
                          item.account_no && item.description && item.amount
                      )
                      .reduce((sum, item) => sum + Number(item.amount || 0), 0)
                  ).toFixed(2)}
                </strong>
              </div>
            ) : (
              <div>
                <strong>
                  Total Paid:{" "}
                  {Number(
                    fullyPaidData.reduce((sum, item) => {
                      const paidAmount = Number(item?.paidAmount) || 0;
                      return sum + paidAmount;
                    }, 0)
                  ).toFixed(2)}
                </strong>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormModal;
